import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { verificationService } from '../services/verificationService';
import Input from './ui/Input';
import Button from './ui/Button';
import Icon from './AppIcon';

const SignIn = () => {
  const [step, setStep] = useState('login'); // 'login' or 'verify'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [userSession, setUserSession] = useState(null);
  const navigate = useNavigate();

  const handleSignIn = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Step 1: Authenticate user
      const data = await authService?.signIn(email, password);
      const isOwner = await authService?.isOwner();
      
      // If owner, require email verification
      if (isOwner) {
        setUserSession(data);
        
        // Send verification code
        await verificationService?.sendVerificationCode(email);
        
        setStep('verify');
        setError('');
      } else {
        // Regular users go directly to home
        navigate('/home-landing');
      }
    } catch (err) {
      setError(err?.message || 'Failed to sign in. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Verify OTP code
      const result = await verificationService?.verifyCode(email, otpCode);
      
      if (result?.success) {
        // Verification successful, navigate to dashboard
        navigate('/admin-dashboard');
      } else {
        setError(result?.message || 'Invalid verification code');
      }
    } catch (err) {
      setError(err?.message || 'Failed to verify code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    setLoading(true);

    try {
      await verificationService?.sendVerificationCode(email);
      setError('');
      // Show success message briefly
      const successMsg = 'New verification code sent to your email';
      setError(successMsg);
      setTimeout(() => {
        if (error === successMsg) setError('');
      }, 3000);
    } catch (err) {
      setError(err?.message || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setStep('login');
    setOtpCode('');
    setError('');
    setUserSession(null);
  };

  // Login Step
  if (step === 'login') {
    return (
      <div className="min-h-screen flex items-center justify-center relative overflow-hidden px-4 py-12" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)' }}>
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-purple-400 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-float"></div>
          <div className="absolute top-40 right-10 w-72 h-72 bg-pink-400 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-float" style={{ animationDelay: '2s' }}></div>
          <div className="absolute -bottom-20 left-1/2 w-72 h-72 bg-indigo-400 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-float" style={{ animationDelay: '4s' }}></div>
        </div>

        <div className="max-w-md w-full relative z-10">
          <div className="bg-white/95 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-white/20">
            <div className="text-center mb-8">
              <div className="w-24 h-24 rounded-full overflow-hidden mx-auto mb-4 border-4 border-purple-500 shadow-xl">
                <img 
                  src="/assets/images/WhatsApp_Image_2026-01-14_at_9.51.56_AM-1768413165356.jpeg" 
                  alt="Edrian Bass"
                  className="w-full h-full object-cover"
                />
              </div>
              <h1 className="text-4xl font-bold mb-2" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Welcome Back</h1>
              <p className="text-gray-600 font-medium">Sign in to access your account</p>
            </div>

            <form onSubmit={handleSignIn} className="space-y-6">
              <Input
                label="Email Address"
                type="email"
                placeholder="your.email@example.com"
                value={email}
                onChange={(e) => setEmail(e?.target?.value)}
                required
              />

              <Input
                label="Password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e?.target?.value)}
                required
              />

              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                  <p className="text-red-600 text-sm font-medium">{error}</p>
                </div>
              )}

              <Button
                type="submit"
                loading={loading}
                fullWidth
                iconName="LogIn"
                iconPosition="right"
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold py-3 rounded-xl shadow-lg hover:shadow-xl transition-all"
              >
                Sign In
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Verification Step
  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden px-4 py-12" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)' }}>
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-purple-400 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-float"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-pink-400 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-float" style={{ animationDelay: '2s' }}></div>
        <div className="absolute -bottom-20 left-1/2 w-72 h-72 bg-indigo-400 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-float" style={{ animationDelay: '4s' }}></div>
      </div>

      <div className="max-w-md w-full relative z-10">
        <div className="bg-white/95 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-white/20">
          <div className="text-center mb-8">
            <div className="w-24 h-24 rounded-full overflow-hidden mx-auto mb-4 border-4 border-purple-500 shadow-xl">
              <img 
                src="/assets/images/WhatsApp_Image_2026-01-14_at_9.51.56_AM-1768413165356.jpeg" 
                alt="Edrian Bass"
                className="w-full h-full object-cover"
              />
            </div>
            <h1 className="text-4xl font-bold mb-2" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Email Verification</h1>
            <p className="text-gray-600 font-medium">Enter the 6-digit code sent to</p>
            <p className="text-purple-700 font-semibold mt-1">{email}</p>
          </div>

          <form onSubmit={handleVerifyOTP} className="space-y-6">
            <Input
              label="Verification Code"
              type="text"
              placeholder="000000"
              value={otpCode}
              onChange={(e) => setOtpCode(e?.target?.value?.replace(/\D/g, '')?.slice(0, 6))}
              maxLength={6}
              required
              className="text-center text-2xl tracking-widest"
            />

            {error && (
              <div className={`p-4 border rounded-xl ${
                error?.includes('sent to your email') 
                  ? 'bg-green-50 border-green-200' :'bg-red-50 border-red-200'
              }`}>
                <p className={`text-sm font-medium ${
                  error?.includes('sent to your email') 
                    ? 'text-green-700' :'text-red-600'
                }`}>{error}</p>
              </div>
            )}

            <Button
              type="submit"
              loading={loading}
              fullWidth
              iconName="CheckCircle"
              iconPosition="right"
              disabled={otpCode?.length !== 6}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold py-3 rounded-xl shadow-lg hover:shadow-xl transition-all"
            >
              Verify Code
            </Button>
          </form>

          <div className="mt-6 space-y-3">
            <button
              type="button"
              onClick={handleResendCode}
              disabled={loading}
              className="w-full text-sm text-purple-700 hover:text-purple-900 font-semibold transition-colors disabled:opacity-50"
            >
              Resend Verification Code
            </button>
            
            <button
              type="button"
              onClick={handleBackToLogin}
              className="w-full text-sm text-gray-600 hover:text-gray-900 transition-colors flex items-center justify-center gap-2 font-medium"
            >
              <Icon name="ArrowLeft" size={16} />
              Back to Login
            </button>
          </div>

          <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200">
            <p className="text-xs text-gray-600 text-center">
              The verification code will expire in 10 minutes. If you did not receive the email, check your spam folder.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;